/**
 * PricingToggleContext — global "show prices" gate (Stage 5 / CAT-003).
 *
 * Lives in `platform/` rather than under a feature because it is app-global by
 * construction: the provider is mounted by the app shell (`AppLayout`) on every
 * authenticated route and the toggle is read from the user menu, so keeping it
 * inside `catalog/browse` made the whole catalog feature reachable from the
 * entry chunk.
 *
 * Some tenants share devices with end-customers in showrooms or kiosks and
 * need a one-tap hide for sensitive pricing. The toggle state is persisted
 * to `sessionStorage` (per browser tab) and broadcast via React Context so
 * every card in `ResultGrid` (and the PDP pricing stack) updates in lock-
 * step.
 *
 * Default = `true` (prices visible). Tenants that want it default-off can
 * pre-seed `sessionStorage` via a tenant policy script.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

// Retains the legacy `catalog:` namespace deliberately even though this module
// moved out of the catalog feature — renaming it would silently reset the
// persisted toggle for every user with an active session.
const STORAGE_KEY = 'aq-cloud2:catalog:pricing-visible'

interface PricingToggleContextValue {
  visible: boolean
  setVisible: (next: boolean) => void
  toggle: () => void
}

const Context = createContext<PricingToggleContextValue | null>(null)

export interface PricingToggleProviderProps {
  children: ReactNode
  /** Override the initial value (mainly for tests / SSR). */
  initialVisible?: boolean
}

export function PricingToggleProvider({
  children,
  initialVisible,
}: PricingToggleProviderProps): JSX.Element {
  const [visible, setVisibleRaw] = useState<boolean>(() => {
    if (typeof initialVisible === 'boolean') return initialVisible
    if (typeof window === 'undefined') return true
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY)
      if (stored === 'true') return true
      if (stored === 'false') return false
    } catch {
      // sessionStorage can throw in private-mode / iframes — fall back to default.
    }
    return true
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem(STORAGE_KEY, visible ? 'true' : 'false')
    } catch {
      // ignore — best effort
    }
  }, [visible])

  const setVisible = useCallback((next: boolean) => setVisibleRaw(next), [])
  const toggle = useCallback(() => setVisibleRaw((p) => !p), [])
  const value = useMemo<PricingToggleContextValue>(
    () => ({ visible, setVisible, toggle }),
    [visible, setVisible, toggle],
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

/**
 * Consume the pricing-visibility context. Safe to call outside a provider:
 * returns `{ visible: true, ...no-ops }` so components keep rendering and
 * the user gets the default-visible behavior.
 */
export function usePricingToggle(): PricingToggleContextValue {
  const ctx = useContext(Context)
  if (ctx) return ctx
  return {
    visible: true,
    setVisible: () => undefined,
    toggle: () => undefined,
  }
}
